import React, { useState } from 'react';
import styled from 'styled-components';
import { Button } from './common/Button';
import { LoadingSpinner } from './common/LoadingSpinner';
import { theme } from '../styles/theme';

const DemoContainer = styled.div`
  padding: ${theme.spacing['2xl']};
  max-width: 1200px;
  margin: 0 auto;
`;

const Title = styled.h2`
  font-size: ${theme.fontSizes['2xl']};
  color: ${theme.colors.text.primary};
  text-align: center;
  margin-bottom: ${theme.spacing.xl};
`;

const DemoGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${theme.spacing['2xl']};
  
  @media (min-width: ${theme.breakpoints.lg}) {
    grid-template-columns: 1fr 1fr;
  }
`;

const Section = styled.div`
  background: ${theme.colors.background};
  border: 2px solid ${theme.colors.border};
  border-radius: ${theme.radii.lg};
  padding: ${theme.spacing.xl};
`;

const SectionTitle = styled.h3`
  font-size: ${theme.fontSizes.lg};
  color: ${theme.colors.primary};
  margin-bottom: ${theme.spacing.md};
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
`;

const PromptInput = styled.textarea`
  width: 100%;
  min-height: 80px;
  padding: ${theme.spacing.md};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radii.md};
  font-size: ${theme.fontSizes.base};
  font-family: ${theme.fonts.body};
  resize: vertical;
  margin-bottom: ${theme.spacing.md};
  
  &:focus {
    outline: none;
    border-color: ${theme.colors.primary};
  }
`;

const TemplateBox = styled.div`
  background: ${theme.colors.backgroundSecondary};
  padding: ${theme.spacing.md};
  border-radius: ${theme.radii.md};
  margin: ${theme.spacing.md} 0;
  font-family: 'Monaco', monospace;
  font-size: ${theme.fontSizes.sm};
  line-height: 1.8;
  white-space: pre-wrap;
`;

const BlankContainer = styled.div`
  margin: ${theme.spacing.md} 0;
  padding: ${theme.spacing.md};
  background: ${theme.colors.primaryLight}20;
  border-radius: ${theme.radii.md};
  border: 1px solid ${theme.colors.primary}30;
`;

const BlankLabel = styled.label`
  display: block;
  font-size: ${theme.fontSizes.sm};
  color: ${theme.colors.text.secondary};
  margin-bottom: ${theme.spacing.xs};
  font-weight: 500;
`;

const BlankSelect = styled.select`
  width: 100%;
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radii.sm};
  font-size: ${theme.fontSizes.base};
  background: white;
  cursor: pointer;
  
  &:focus {
    outline: none;
    border-color: ${theme.colors.primary};
  }
`;

const BlankInput = styled.input`
  width: 100%;
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radii.sm};
  font-size: ${theme.fontSizes.base};
  
  &:focus {
    outline: none;
    border-color: ${theme.colors.primary};
  }
`;

const ResultBox = styled.div`
  background: ${theme.colors.backgroundSecondary};
  padding: ${theme.spacing.lg};
  border-radius: ${theme.radii.md};
  margin-top: ${theme.spacing.md};
  border: 2px solid ${theme.colors.primary}30;
`;

const ComparisonSection = styled.div`
  margin-top: ${theme.spacing['2xl']};
  padding: ${theme.spacing.xl};
  background: ${theme.colors.backgroundSecondary};
  border-radius: ${theme.radii.lg};
`;

const ScoreBar = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.md};
  margin-top: ${theme.spacing.sm};
`;

const ScoreLabel = styled.span`
  font-size: ${theme.fontSizes.sm};
  color: ${theme.colors.text.secondary};
  min-width: 100px;
`;

const ScoreProgress = styled.div`
  flex: 1;
  height: 8px;
  background: ${theme.colors.border};
  border-radius: 999px;
  overflow: hidden;
`;

const ScoreFill = styled.div<{ $width: number; $color?: string }>`
  height: 100%;
  width: ${props => props.$width}%;
  background: ${props => props.$color || theme.colors.primary};
  transition: width 0.5s ease;
`;

export const FossilFillsDemo: React.FC = () => {
  const [userPrompt, setUserPrompt] = useState('Create a pitch for Don and Peggy from Mad Men about a new campaign');
  const [blanks, setBlanks] = useState({
    role: 'experienced_screenwriter',
    format: 'dialogue_scene',
    style: 'dramatic_with_subtext',
    tone: 'professional_yet_tense',
    goal: 'advance_character_development'
  });
  const [showComparison, setShowComparison] = useState(false);

  // Fossil Fills template options
  const templateOptions = {
    role: [
      { value: 'experienced_screenwriter', label: 'Experienced Screenwriter' },
      { value: 'creative_director', label: 'Creative Director (like Don)' },
      { value: 'copywriter', label: 'Copywriter (like Peggy)' },
      { value: 'narrative_expert', label: 'Narrative Expert' },
      { value: 'custom', label: 'Custom Role...' }
    ],
    format: [
      { value: 'dialogue_scene', label: 'Dialogue Scene' },
      { value: 'pitch_presentation', label: 'Pitch Presentation' },
      { value: 'internal_monologue', label: 'Internal Monologue' },
      { value: 'screenplay_format', label: 'Screenplay Format' },
      { value: 'narrative_prose', label: 'Narrative Prose' }
    ],
    style: [
      { value: 'dramatic_with_subtext', label: 'Dramatic with Subtext' },
      { value: 'witty_banter', label: 'Witty Banter' },
      { value: 'tense_confrontation', label: 'Tense Confrontation' },
      { value: 'subtle_power_play', label: 'Subtle Power Play' },
      { value: 'authentic_period_dialogue', label: 'Authentic 1960s Style' }
    ],
    tone: [
      { value: 'professional_yet_tense', label: 'Professional Yet Tense' },
      { value: 'creative_collaboration', label: 'Creative Collaboration' },
      { value: 'mentor_student_dynamic', label: 'Mentor-Student Dynamic' },
      { value: 'competitive_rivalry', label: 'Competitive Rivalry' },
      { value: 'respectful_disagreement', label: 'Respectful Disagreement' }
    ],
    goal: [
      { value: 'advance_character_development', label: 'Advance Character Development' },
      { value: 'reveal_creative_process', label: 'Reveal Creative Process' },
      { value: 'showcase_advertising_brilliance', label: 'Showcase Advertising Brilliance' },
      { value: 'explore_power_dynamics', label: 'Explore Power Dynamics' },
      { value: 'build_dramatic_tension', label: 'Build Dramatic Tension' }
    ]
  };

  const generateFossilFillsPrompt = () => {
    return `You are an ${blanks.role.replace(/_/g, ' ')}.

Your task: ${userPrompt}

Create a ${blanks.format.replace(/_/g, ' ')} that captures the essence of Don and Peggy's relationship.

Style: ${blanks.style.replace(/_/g, ' ')}
Tone: ${blanks.tone.replace(/_/g, ' ')}
Goal: ${blanks.goal.replace(/_/g, ' ')}

Requirements:
• Maintain authentic character voices (Don's authority, Peggy's ambition)
• Include period-appropriate details (1960s Madison Avenue)
• Show both characters' strengths and vulnerabilities
• ${blanks.style === 'dramatic_with_subtext' ? 'Layer meanings beneath surface dialogue' : 'Keep dialogue sharp and engaging'}
• ${blanks.goal === 'advance_character_development' ? 'Show how both characters grow through this interaction' : 'Focus on the creative/business objective'}

The scene should feel like it could be from the actual show, with all the complexity and nuance that entails.`;
  };

  const generatePromptasaurusPrompt = () => {
    return `You are a creative writer with expertise in character-driven narratives.

${userPrompt}

Include:
• Setting and atmosphere
• Character dynamics between Don and Peggy
• Natural dialogue that reveals character
• Visual details
• Emotional subtext
• A turning point in the scene

Focus on authentic voices and meaningful conflict.`;
  };

  const runComparison = () => {
    setShowComparison(true);
  };

  return (
    <DemoContainer>
      <Title>🦕 Fossil Fills Interactive Demo</Title>
      
      <DemoGrid>
        {/* Left: Fossil Fills with Mad Libs */}
        <Section>
          <SectionTitle>
            <span>🦕</span> Fossil Fills (Mad Libs Style)
          </SectionTitle>
          
          <PromptInput
            placeholder="Enter your base prompt..."
            value={userPrompt}
            onChange={(e) => setUserPrompt(e.target.value)}
          />
          
          <div style={{ marginBottom: theme.spacing.md }}>
            <Button 
              variant="secondary" 
              size="small" 
              onClick={() => {
                setUserPrompt('');
                setShowComparison(false);
              }}
            >
              Clear and Start Fresh
            </Button>
          </div>
          
          <div style={{ marginBottom: theme.spacing.lg }}>
            <h4>Fill in the blanks to customize your prompt:</h4>
            
            {Object.entries(templateOptions).map(([key, options]) => (
              <BlankContainer key={key}>
                <BlankLabel>
                  {key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, ' ')}:
                </BlankLabel>
                <BlankSelect
                  value={blanks[key as keyof typeof blanks]}
                  onChange={(e) => setBlanks({ ...blanks, [key]: e.target.value })}
                >
                  {options.map(opt => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </BlankSelect>
              </BlankContainer>
            ))}
          </div>
          
          <ResultBox>
            <strong>Generated Fossil Fills Prompt:</strong>
            <TemplateBox>{generateFossilFillsPrompt()}</TemplateBox>
          </ResultBox>
        </Section>

        {/* Right: Promptasaurus */}
        <Section>
          <SectionTitle>
            <span>✨</span> Promptasaurus (Automatic Enhancement)
          </SectionTitle>
          
          <PromptInput
            value={userPrompt}
            disabled
            style={{ background: theme.colors.backgroundSecondary }}
          />
          
          <div style={{ padding: theme.spacing.lg, textAlign: 'center', color: theme.colors.text.secondary }}>
            <p style={{ marginBottom: theme.spacing.md }}>
              Promptasaurus automatically enhances your prompt without any manual configuration.
            </p>
            <p>
              It analyzes your intent and adds structure, context, and clarity.
            </p>
          </div>
          
          <ResultBox>
            <strong>Generated Promptasaurus Prompt:</strong>
            <TemplateBox>{generatePromptasaurusPrompt()}</TemplateBox>
          </ResultBox>
        </Section>
      </DemoGrid>

      <div style={{ textAlign: 'center', margin: `${theme.spacing['2xl']} 0` }}>
        <Button onClick={runComparison} size="large">
          Compare Results
        </Button>
      </div>

      {showComparison && (
        <ComparisonSection>
          <h3>📊 Comparison Results</h3>
          
          <div style={{ marginTop: theme.spacing.lg }}>
            <ScoreBar>
              <ScoreLabel>Original:</ScoreLabel>
              <ScoreProgress>
                <ScoreFill $width={30} $color="#999" />
              </ScoreProgress>
              <span>30%</span>
            </ScoreBar>
            
            <ScoreBar>
              <ScoreLabel>Fossil Fills:</ScoreLabel>
              <ScoreProgress>
                <ScoreFill $width={85} $color="#10b981" />
              </ScoreProgress>
              <span>85%</span>
            </ScoreBar>
            
            <ScoreBar>
              <ScoreLabel>Promptasaurus:</ScoreLabel>
              <ScoreProgress>
                <ScoreFill $width={75} $color="#667eea" />
              </ScoreProgress>
              <span>75%</span>
            </ScoreBar>
          </div>
          
          <div style={{ marginTop: theme.spacing.xl, padding: theme.spacing.lg, background: 'white', borderRadius: theme.radii.md }}>
            <h4>Key Differences:</h4>
            <ul style={{ marginTop: theme.spacing.md, lineHeight: 1.8 }}>
              <li><strong>Fossil Fills:</strong> Highly customizable with {Object.keys(blanks).length} configurable parameters. Perfect when you know exactly what style and tone you want.</li>
              <li><strong>Promptasaurus:</strong> Zero configuration needed. Automatically enhances based on context. Great for quick improvements.</li>
              <li><strong>Use Case:</strong> Fossil Fills excels for specialized content (like screenwriting), while Promptasaurus is ideal for general prompt enhancement.</li>
            </ul>
          </div>
        </ComparisonSection>
      )}
    </DemoContainer>
  );
};